import {
    createContext, ReactNode, useCallback, useContext, useMemo, useState,
} from 'react';

import { MakeOptional } from 'Components/Common/types/types';
import { EventDto, EventRecurrenceDto } from 'api/dtos/event';

export type ContextEvent = MakeOptional<EventDto, 'id' | 'entityId'> & {
    isEdit: boolean;
    isMultiDay: boolean;
    timeFrom: string;
    timeTo: string;
    recurrence: EventRecurrenceDto;
    optionalPointsPerEvent?: number | null;
    absencePenaltyPoints?: number | null;
    qrConfirmationWindowMinutes: number;
    attendeeReminderMinutesBefore?: number | null;
};

interface EventContextProps {
    values: ContextEvent;
    updateValues: (updated: Partial<ContextEvent>) => void;
    resetValues: (newInitialState: ContextEvent) => void;
  }

export const defaultValues: ContextEvent = {
    title: '',
    description: '',
    eventPlace: '',
    dateFrom: '',
    dateTo: '',
    timeFrom: '',
    timeTo: '',
    email: '',
    recurrence: {
        type: 0,
    },
    howLong: {
        mode: 0,
    },
    pointsPerEvent: 0,
    optionalPointsPerEvent: undefined,
    absencePenaltyPoints: null,
    qrConfirmationWindowMinutes: 60,
    attendeeReminderMinutesBefore: null,
    attendees: [],
    isEdit: false,
    isMultiDay: false,
    categoryIds: [],
    allDay: false,
    visibleForGuests: false,
    kind: 'Local',
    scope: 'LocalOnly',
    targetEntityIds: [],
    color: '#3174AD',
    priests: [],
    priestIds: [],
};

const EventContext = createContext<EventContextProps | undefined>(undefined);

export const EventProvider: React.FC<{
    children: ReactNode;
    initialState?: ContextEvent;
  }> = ({ children, initialState }) => {
      const mergedInitial: ContextEvent = { ...defaultValues, ...initialState };
      const [values, setValues] = useState<ContextEvent>(mergedInitial);

      const updateValues = useCallback((updated: Partial<ContextEvent>) => {
          setValues((prev) => ({ ...prev, ...updated }));
      }, []);

      const resetValues = useCallback((newInitialState: ContextEvent) => {
          setValues(newInitialState);
      }, []);

      const valuesToProvide = useMemo(
          () => ({ values, updateValues, resetValues }),
          [resetValues, updateValues, values],
      );

      return (
          <EventContext.Provider value={valuesToProvide}>
              {children}
          </EventContext.Provider>
      );
  };

export const useEventContext = () => {
    const context = useContext(EventContext);
    if (!context) {
        throw new Error('useEventContext must be used within an EventProvider');
    }
    return context;
};
